import { useRef, useState } from 'react';
import type { Party } from '../types/case';
import { copyText, selectElement } from '../lib/clipboard';
import { formatPhone } from '../lib/format';

export type InviteStatus = 'idle' | 'generating' | 'ready' | 'failed';

export interface InviteState {
  status: InviteStatus;
  joinUrl?: string;
  error?: string;
}

interface Props {
  party: Party;
  /** Omit both invite props to render without the Zoom invite affordance
      (dashboard Case view — invites are meeting-scoped). */
  invite?: InviteState;
  onInvite?: (party: Party) => void;
  /** A2M consent timestamp (from cases.metadata.a2m_consents). Pass null
      for "not yet agreed"; omit entirely to hide the badge (participants,
      sidebar contexts). */
  a2mAt?: string | null;
  /** Present = the viewer may record an offline (verbal) A2M consent for
      this party; rendered only while the amber badge shows. */
  onRecordA2m?: () => void;
  /** Send the party their Agreement to Mediate link (email/SMS). */
  onSendA2m?: () => void;
}

const roleEmoji = (archetype: string): string => {
  switch (archetype) {
    case 'claimant':
      return '👤';
    case 'respondent':
      return '🏢';
    case 'representative':
      return '🧑‍💼';
    case 'mediator':
      return '⚖️';
    case 'participant':
      return '🧑‍🤝‍🧑';
    default:
      return '👤';
  }
};

const buttonLabel = (status: InviteStatus): string => {
  switch (status) {
    case 'generating':
      return 'Generating…';
    case 'ready':
      return 'Regenerate';
    case 'failed':
      return 'Retry';
    default:
      return 'Invite';
  }
};

export function PartyCard({ party, invite = { status: 'idle' }, onInvite, a2mAt, onRecordA2m, onSendA2m }: Props) {
  return (
    <div className="card p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span aria-hidden>{roleEmoji(party.role_archetype)}</span>
            <span className="font-semibold">{party.name}</span>
            <span className="text-xs text-gray-500">
              ({typeof party.metadata?.relationship === 'string' ? party.metadata.relationship : party.role})
            </span>
            {(() => {
              const flags = (party.metadata?.delivery_flags ?? {}) as Record<string, { failed_at?: string; detail?: string }>;
              return (['email', 'sms'] as const)
                .filter((ch) => flags[ch])
                .map((ch) => (
                  <span
                    key={ch}
                    className="rounded-full bg-red-100 px-1.5 py-0.5 text-[11px] font-medium text-red-800"
                    title={`${ch === 'email' ? 'Email' : 'Text'} undeliverable since ${flags[ch]?.failed_at ? new Date(flags[ch]!.failed_at!).toLocaleString() : '?'} — ${flags[ch]?.detail ?? 'delivery failed'}. Fix the contact info; the flag clears on the next successful delivery.`}
                  >
                    ⚠ {ch === 'email' ? 'email bouncing' : 'texts failing'}
                  </span>
                ));
            })()}
            {a2mAt !== undefined &&
              (a2mAt ? (
                <span
                  className="rounded-full bg-green-100 px-1.5 py-0.5 text-[11px] font-medium text-green-800"
                  title={`Agreed to mediate ${new Date(a2mAt).toLocaleString()}`}
                >
                  ✓ Agreed to mediate
                </span>
              ) : (
                <span className="inline-flex items-center gap-1">
                  <span
                    className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800"
                    title="This party has not yet accepted the Agreement to Mediate (the consent that lets mediation proceed) — unrelated to any settlement document"
                  >
                    Awaiting Agreement to Mediate
                  </span>
                  {onSendA2m && (
                    <button
                      type="button"
                      className="rounded bg-brand px-2 py-0.5 text-[11px] font-medium text-white hover:opacity-90"
                      title="Email/text this party the Agreement to Mediate terms with an I-agree link. Their acceptance is recorded automatically."
                      onClick={onSendA2m}
                    >
                      Send Agreement to Mediate
                    </button>
                  )}
                  {onRecordA2m && (
                    <button
                      type="button"
                      className="text-[11px] text-gray-400 underline decoration-dotted hover:text-brand hover:decoration-solid"
                      title="Last resort when they can't use the link: they agreed verbally, on paper, or on the old platform. Recorded in the audit trail under your name."
                      onClick={onRecordA2m}
                    >
                      record verbal (last resort)…
                    </button>
                  )}
                </span>
              ))}
          </div>
          {typeof party.metadata?.business_name === 'string' && party.metadata.business_name && (
            <div className="mt-0.5 text-xs text-gray-600">
              <span aria-hidden>🏢 </span>
              {typeof party.metadata?.business_role === 'string' && party.metadata.business_role
                ? `${party.metadata.business_role}, ${party.metadata.business_name}`
                : party.metadata.business_name}
            </div>
          )}
          {party.phone && (
            <div className="mt-1 text-sm text-gray-600">
              <span aria-hidden>📱 </span>
              {formatPhone(party.phone)}
            </div>
          )}
          {party.email && (
            <div className="mt-0.5 text-sm text-gray-600">
              <span aria-hidden>✉️ </span>
              <span className="break-all">{party.email}</span>
            </div>
          )}
          {party.communication_preference && (
            <div className="mt-0.5 text-xs text-gray-500">
              Prefers {party.communication_preference}
            </div>
          )}
        </div>
        {onInvite && (
          <button
            type="button"
            className="btn-primary shrink-0"
            disabled={invite.status === 'generating'}
            onClick={() => onInvite(party)}
          >
            {buttonLabel(invite.status)}
          </button>
        )}
      </div>

      {invite.status === 'ready' && invite.joinUrl && (
        <InviteLink url={invite.joinUrl} />
      )}

      {invite.status === 'failed' && (
        <div className="mt-2 text-xs text-red-600">
          {invite.error ?? 'Failed to generate invite'}
        </div>
      )}
    </div>
  );
}

type CopyLabel = 'Copy' | 'Copied ✓' | '⌘C to copy';

function InviteLink({ url }: { url: string }) {
  const [label, setLabel] = useState<CopyLabel>('Copy');
  const urlRef = useRef<HTMLDivElement>(null);

  const onCopy = async () => {
    const result = await copyText(url);
    if (result === 'failed') {
      if (urlRef.current) selectElement(urlRef.current);
      setLabel('⌘C to copy');
      window.setTimeout(() => setLabel('Copy'), 3000);
      return;
    }
    setLabel('Copied ✓');
    window.setTimeout(() => setLabel('Copy'), 1500);
  };

  return (
    <div className="mt-3 rounded-md border border-surface-border bg-surface-muted p-2">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-medium text-gray-600">Personal join link</div>
        <button type="button" className="btn-secondary !py-1 !text-xs" onClick={onCopy}>
          {label}
        </button>
      </div>
      <div
        ref={urlRef}
        className="mt-1.5 break-all font-mono text-xs text-gray-700 select-all"
        title={url}
      >
        {url}
      </div>
      <div className="mt-1.5 text-[11px] text-gray-500">
        Link expires in 2 hours · SMS delivery coming soon
      </div>
    </div>
  );
}
